/**
 * Typed client for the server-side paper-trading API — frontend phase 5.
 * With H4 phase 2 complete, ALL fills happen server-side (entries, resting
 * matches, and — as of the H4-2 remainder — closes); the page's job is to
 * place, cancel, close, and render the returned state. This module is that
 * client, testable against a fake http function; static/paper.html wires it
 * with its own apiFetch (auth + 401 handling).
 */

export interface PaperHttpResponse {
  ok: boolean;
  status: number;
  json(): Promise<any>;
}
export type PaperHttp = (url: string, init?: {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
}) => Promise<PaperHttpResponse>;

export interface PaperApiResult {
  status: string;
  state: any;
  version: number;
  order?: any;
  orders?: any[];
  basket?: any;
  basketId?: string;
  result?: any;
  fills?: any[];
  fill?: any;
  count?: number;
}

export class PaperApiError extends Error {
  constructor(public detail: string, public httpStatus: number) {
    super(detail);
  }
  /** "Close skipped" class — already flat / no live price — vs a real failure. */
  get isSkippable(): boolean {
    return /already flat|No live price/i.test(this.detail);
  }
}

export interface PlaceOrderReq {
  symbol: string;
  optType?: string;
  side: "BUY" | "SELL";
  qty: number;
  orderType: "MARKET" | "LIMIT" | "SL-M" | "SL-L";
  limitPrice?: number | null;
  triggerPrice?: number | null;
  product?: string;
  expiry?: string;
}

export interface BasketLegReq {
  symbol: string;
  side: "BUY" | "SELL";
  qty: number;
  optType?: string;
  strike?: number | null;
  orderType?: "MARKET" | "LIMIT";
  limitPrice?: number | null;
  product?: string;
  expiry?: string;
}

export interface PlaceBasketOrderReq {
  name?: string;
  basketName?: string;
  targetProfit?: number | null;
  stopLoss?: number | null;
  legs: BasketLegReq[];
}

export interface CloseBasketReq {
  reason?: string;
}

export interface ClosePositionReq {
  symbol: string;
  product?: string;
  reason?: string;
  price?: number | null;
}

async function post(http: PaperHttp, url: string, body?: object): Promise<PaperApiResult> {
  const resp = await http(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new PaperApiError(String(data.detail ?? resp.status), resp.status);
  }
  return data as PaperApiResult;
}

export function createPaperApi(http: PaperHttp) {
  return {
    placeOrder: (req: PlaceOrderReq) => post(http, "/api/paper/order", req),
    cancelOrder: (orderId: string) =>
      post(http, `/api/paper/order/${encodeURIComponent(orderId)}/cancel`),
    cancelAll: () => post(http, "/api/paper/orders/cancel-all"),
    closePosition: (req: ClosePositionReq) => post(http, "/api/paper/position/close", req),
    placeBasketOrder: (req: PlaceBasketOrderReq) => post(http, "/api/paper/basket/order", req),
    closeBasket: (basketId: string, req?: CloseBasketReq) =>
      post(http, `/api/paper/basket/${encodeURIComponent(basketId)}/close`, req ?? {}),
  };
}
export type PaperApi = ReturnType<typeof createPaperApi>;
